import React from 'react';
import {Box} from '@mui/material';
import axios from 'axios';
import {useSelector} from 'react-redux';
import {useNavigate, useLocation} from 'react-router-dom';
import Api from '@api';
import {
  subscribe,
  unsubscribe,
  send,
  getConnectionId,
  getConnectionStatus,
  onReconnect,
  offReconnect,
} from '@common/socketUtil';

// Trips that need a female security escort and have none assigned. Pushed by the live
// locator service, which watches the trip collection and recounts per corporate — see
// services/escortPending.service.js there for what is counted.
const ESCORT_PENDING_EVENT = 'escort-pending';

// Whoever can allocate on the Route List can assign an escort, so they are the ones this
// is for. A view-only route user gets nothing. Read from the permissions already in the
// store — the same lookup the Route List itself uses to gate View.
const ROUTE_LIST_SUBMODULE = 'Route List';
const ALLOCATE_ACTION = 'Allocate And Deallocate';

// Asked for when the socket cannot answer. How often to re-ask while it is down - slow, because
// nothing is listening for changes and this only exists to keep the figure from going stale.
const OFFLINE_REFRESH_MS = 60000;

const EscortPendingBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = useSelector(({settings}) => settings.permissions);
  const [counts, setCounts] = React.useState(null);
  // Whether a live figure has arrived. A fetch that comes back after one must not overwrite it.
  const liveFigureSeen = React.useRef(false);

  const applyCounts = React.useCallback((data) => {
    // A dropped socket must not read as "nothing pending". Only a payload that actually carries
    // a figure replaces what is on screen, so a reconnect or a malformed frame leaves the last
    // known count rather than clearing the banner while we are blind.
    if (typeof data?.total !== 'number') {
      return;
    }
    setCounts({
      total: data.total,
      active: Number(data.active) || 0,
      scheduled: Number(data.scheduled) || 0,
    });
  }, []);

  // The count is pushed, so this is only the floor: it gives the banner a value when the socket
  // has not answered - the service down, the upgrade refused, the token stale - where otherwise
  // there would be no banner at all rather than an out-of-date one.
  const fetchCounts = React.useCallback(() => {
    axios
      .get(Api.baseUri + '/user-reg/trip-route/escort-pending-count')
      .then((res) => {
        if (liveFigureSeen.current) {
          return;
        }
        applyCounts(res?.data?.data);
      })
      .catch(() => {
        // Nothing to show and nothing to say - the boarding gate holds regardless of whether
        // this banner is on screen.
      });
  }, [applyCounts]);

  const canAllocate = React.useMemo(() => {
    if (!Array.isArray(permissions)) {
      return false;
    }
    const routeList = permissions.find(
      (p) => p?.subModuleName === ROUTE_LIST_SUBMODULE,
    );
    return Boolean(routeList?.actions?.includes(ALLOCATE_ACTION));
  }, [permissions]);

  React.useEffect(() => {
    if (!canAllocate) {
      return undefined;
    }
    const onMessage = (data) => {
      if (data?.type !== ESCORT_PENDING_EVENT || typeof data.total !== 'number') {
        return;
      }
      liveFigureSeen.current = true;
      applyCounts(data);
    };
    subscribe(onMessage);

    // The socket is opened at the app root on login, and the server pushes the count the
    // moment it registers - before this component exists. So ask for it on mount rather
    // than wait for the next trip change, which could be hours away, and ask again after
    // every reconnect because the same race repeats there.
    const request = () => {
      const corporateId = getConnectionId();
      if (corporateId) {
        send({type: 'get-escort-pending', corporateId});
      }
    };
    request();
    onReconnect(request);

    // Seeded immediately, because the socket may never answer at all.
    fetchCounts();

    // And re-asked while it stays down: no pushes are arriving, so without this the figure
    // would be whatever it was when the connection dropped, for as long as the tab is open.
    const offlineTimer = window.setInterval(() => {
      if (!getConnectionStatus().connected) {
        liveFigureSeen.current = false;
        fetchCounts();
      }
    }, OFFLINE_REFRESH_MS);

    return () => {
      unsubscribe(onMessage);
      offReconnect(request);
      window.clearInterval(offlineTimer);
    };
  }, [canAllocate, applyCounts, fetchCounts]);

  // The public tracking route renders the content view with no portal chrome around it.
  const isPublic = location.pathname.startsWith('/public/');

  if (!canAllocate || isPublic || !counts || counts.total < 1) {
    return null;
  }

  const {total, active, scheduled} = counts;
  const urgent = active > 0;
  const parts = [];
  if (active) parts.push(`${active} Active`);
  if (scheduled) parts.push(`${scheduled} Scheduled`);

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mx: '20px',
        mt: '20px',
        // A real gap, not zero. Pages paint their own background in the box below and it meets
        // the banner's bottom edge exactly, so the border reads as missing without one.
        mb: '12px',
        px: 1.75,
        py: 1.25,
        borderRadius: '8px',
        bgcolor: urgent ? '#FEECEB' : '#FFF3E0',
        border: `1px solid ${urgent ? '#f3c9c6' : '#f5d8b4'}`,
        color: urgent ? '#C62828' : '#E65100',
      }}
    >
      <Box sx={{display: 'flex', flexShrink: 0}}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2 4 5v6c0 5 3.4 9.3 8 11 4.6-1.7 8-6 8-11V5l-8-3Zm-1 5h2v6h-2V7Zm0 8h2v2h-2v-2Z" />
        </svg>
      </Box>

      <Box sx={{flex: 1, minWidth: 0, lineHeight: 1.4}}>
        <Box sx={{fontWeight: 600, fontSize: '15px'}}>
          {total === 1 ? '1 trip needs an escort' : `${total} trips need an escort`}
        </Box>
        <Box sx={{fontSize: '13px', color: '#333', opacity: 0.9}}>
          {parts.join(' + ')} — no escort assigned
        </Box>
      </Box>

      <Box
        component="button"
        type="button"
        // The Route List reads this category off the query string and drops its date
        // filter for it, so the rows shown match the number counted here.
        onClick={() => navigate('/route-lists/ALL?category=ESCORT_PENDING')}
        sx={{
          font: 'inherit',
          fontSize: '13px',
          fontWeight: 600,
          cursor: 'pointer',
          px: 1.75,
          py: 0.75,
          borderRadius: '6px',
          border: 0,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          color: '#fff',
          bgcolor: urgent ? '#C62828' : '#E65100',
        }}
      >
        View trips
      </Box>
    </Box>
  );
};

export default EscortPendingBanner;
